<script setup lang="ts">
import type { Component } from 'vue'
import { computed } from 'vue'
import { Palette, type PaletteName } from '@/design-system/components'

/**
 * TodayWidget (§03) — the shared widget-rail card: hue-soft icon tile + eyebrow
 * + optional count in the header, body slot, and an optional footer-action slot.
 */
const props = defineProps<{
  icon: Component
  iconHue: PaletteName
  title: string
  count?: number | null
}>()

const hue = computed(() => Palette[props.iconHue])
</script>

<template>
  <div class="widget">
    <div class="widget__head">
      <span class="widget__tile" :style="{ background: hue.soft, color: hue.ink }">
        <component :is="icon" :size="14" />
      </span>
      <span class="widget__eyebrow">{{ title }}</span>
      <span v-if="count != null" class="widget__count">{{ count }}</span>
    </div>
    <div class="widget__body"><slot /></div>
    <div v-if="$slots.footer" class="widget__footer"><slot name="footer" /></div>
  </div>
</template>

<style scoped>
.widget {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  font-family: var(--font-sans);
}
.widget__head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 14px 16px 10px;
}
.widget__tile {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.widget__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-500);
}
.widget__count {
  margin-left: auto;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: var(--ink-400);
}
.widget__body {
  padding: 0 16px 6px;
}
.widget__footer {
  border-top: 1px solid var(--hairline);
  padding: 11px 16px;
}
</style>
